import React, { useState } from 'react';
import NeedTiles from './NeedTiles';
import PartyChips from './PartyChips';
import TimeChips from './TimeChips';
import { BTN_PRIMARY } from '../liStyles';

// The signature element (§4.12): turn a guest's need into the three places to send
// them right now, with the sentence to say and the button to act.
//
// Needs are tiles, the party is chips, the time is chips; typing is the fallback and
// the database reads the words ("dinner for six with kids" sets the party itself and
// `understood_label` says what it understood). Party and time persist for the session
// and apply to every ask. Keyboard: `/` focuses the input (wired on the page). The
// answer list (with its j / k / Enter nav) now renders in the page's full-width
// results container (Item 3), not inside this console.

// Item 1 — leading icon for the ASK group header, matching the Party/Access
// (PartyChips HeaderIcon) and When (TimeChips ClockIcon) group-header icons:
// an inline <svg> mirroring the lucide-react "message-square" shape (the
// codebase convention; lucide-react is not a dependency).
const AskIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Item 1 — leading icon for the TYPE OF PLACE group header, matching the
// Party/Access (PartyChips HeaderIcon), When (TimeChips ClockIcon), and Ask
// (AskIcon) group-header icons: an inline <svg> mirroring the lucide-react
// "layout-grid" shape (the codebase convention; lucide-react is not a
// dependency). The 2×2 grid echoes the need-tile grid directly below the label,
// which is exactly what the label names.
const TypeOfPlaceIcon = () => (
  <svg className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </svg>
);

const AskConsole = ({
  panel, ask, inputRef, activeNeed, asOf, whenHint, timeOptions,
  onAsk, onParty, onTime, onClear, onNeedSelect,
}) => {
  const facts = panel?.facts || {};
  const [text, setText] = useState('');
  // work-page-fixes Item 4 — the When pills render the PAGE's augmented
  // time_options (the page inserts the synthesized lunch slots, "Today 12 PM"
  // and "Tomorrow 12 PM") so the rendered pills and the page's timeIndex math
  // (runAsk, resolveWhenForNeed) can never drift. Falls back to the panel's
  // own list when the page doesn't pass one.
  const whenOptions = Array.isArray(timeOptions) && timeOptions.length > 0
    ? timeOptions
    : (Array.isArray(facts.time_options) ? facts.time_options : []);
  const partyOptions = Array.isArray(facts.party_options) ? facts.party_options : [];

  const submitText = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    onAsk({ need: t, needKey: null });
    onNeedSelect(t);
  };

  const partyLabels = partyOptions.filter((o) => ask.party?.[o.key] !== undefined).map((o) => o.label);
  // Item 1 — Clear shows when ANY filter is active: a need tile is selected
  // (activeNeed), a party/access chip is on (partyLabels), or When is non-
  // default (ask.timeIndex > 0). Hides only when all are at defaults.
  const narrowed = !!activeNeed || partyLabels.length > 0 || ask.timeIndex > 0;

  return (
    // Item 1 — AskConsole now renders ONLY the filter content (need tiles,
    // party/access/when controls, ask box) as the LEFT column of the page's
    // seamless "What does the guest need?" panel. The panel container
    // (ring + padding), the title, and the sublabel moved up to the page so the
    // title can span the full panel width above both columns. No card/ring/
    // padding here — the page panel owns the chrome.
    <div className="space-y-3">
      {/* Item 1 — a TYPE OF PLACE label sits directly above the need-tile grid,
          naming the tile groups below it (MEALS / THINGS TO DO / ERRANDS &
          SERVICES). The party/access/when controls have their own group labels
          (PARTY / ACCESS / WHEN) and are no longer covered by this label.
          Item 2 (spacing) — one uniform inter-group gap (space-y-3) across every
          filter-group boundary this console controls: tiles → When, When →
          Party, Access → ask box (the form below is a sibling of the groups,
          so it gets the same gap; the When-first order is work-page-fixes
          Item 4). The TYPE OF PLACE label sits closer to the
          tiles it labels (space-y-2, slightly tighter than the inter-group
          spacing). The Party → Access gap lives inside PartyChips (not edited
          here). The label-to-chips spacing within each group is left as each
          group component defines it — only the gaps between groups change. */}
      <div className="space-y-2">
        {/* Item 1 — Clear button next to the TYPE OF PLACE label, right-aligned
            on the same line. Shows only when a filter is active — a need tile is
            selected, a party/access chip is active, or the When option is
            non-default (the `narrowed` flag). Small bordered button at the
            label's 11px scale (matches the codebase's bordered neutral
            treatment — see liStyles BTN / chipClass), not a bare link. The
            handler (onClear, from the page) resets the need tile, party chips,
            and When back to defaults. */}
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5">
            <TypeOfPlaceIcon />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">Type of Place</span>
          </span>
          {narrowed && (
            <button
              type="button"
              onClick={onClear}
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium rounded-md border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 px-2 py-0.5 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <NeedTiles
          tiles={facts.need_tiles}
          activeNeed={activeNeed}
          onPick={(t) => {
            onAsk({ need: t.need_key, needKey: t.need_key });
            onNeedSelect(activeNeed === t.need_key ? null : t.need_key);
          }}
        />
      </div>
      {/* work-page-fixes Item 4 — the When row now LEADS the pill filters
          (When → Party → Access) because it carries the most options — six,
          with the two synthesized lunch pills ("Today 12 PM" after "Now",
          "Tomorrow 12 PM" after "Tomorrow 8 AM") completing the today/
          tomorrow meal grid. asOf is the property's time (from the page's
          as_of field) passed through so the "Now" pill shows the property's
          clock, not the browser's (work-page-fixes Item 1). */}
      <TimeChips options={whenOptions} activeIndex={ask.timeIndex} onChange={onTime} asOf={asOf} />
      {/* work-page-fixes Item 4 — brief muted hint under the When pills when
          the meal/When guardrail shifted the option ("Adjusted to Tonight 7 PM
          for dinner."). The page owns the text and its lifetime (fades after a
          few seconds or on the next selection); this is just the muted slot. */}
      {whenHint && <p className="text-xs text-gray-500 dark:text-gray-400">{whenHint}</p>}
      <PartyChips options={partyOptions} party={ask.party} onChange={onParty} />

      {/* Item 1 — ASK label above the typed-question input, matching the
          Party/Access/When group headers: a leading icon + an uppercase
          text-xs font-semibold tracking-wide gray label. Wrapped in
          space-y-1 so the label-to-input gap matches the label-to-chips
          gap those groups use. pt-3 adds extra separation from the
          When filters above so the ask box reads as its own group
          rather than a continuation of the filter row. */}
      <div className="space-y-1 pt-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <AskIcon />
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 shrink-0">Ask</span>
        </div>
        <form onSubmit={submitText} className="flex gap-2" role="search">
          <input
            ref={inputRef}
            type="search"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Or type what the guest asked…"
            aria-label="Type what the guest asked"
            className="flex-1 min-w-0 h-10 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#32B1FF]/50"
          />
          <button type="submit" className={BTN_PRIMARY} disabled={!text.trim()}>Ask</button>
        </form>
      </div>
    </div>
  );
};

export default AskConsole;
